import type {
  Circuit,
  CircuitDecor3d,
  CircuitDecor3dKind,
  CircuitGrandstand,
  CircuitSign,
  Direction,
} from '../types/track.js';
import { cellCoordKey } from './circuitEdit.js';
import {
  canPlaceAmbientCell,
  grandstandCellKeys,
  isDecor3dCell,
  isGrandstandCell,
  isInfrastructureCell,
  isPropCell,
  isSignCell,
  isWithinGrid,
} from './circuitOccupancy.js';
import { findVehicleAt, pruneCircuitVehicles, removeVehicleAt } from './circuitVehicleEdit.js';
import { findRoadPieceAt, removeRoadPiece } from './circuitRoadPieceEdit.js';

export const MAX_SIGN_TEXT_LENGTH = 60;
export const DEFAULT_SIGN_LABEL = 'Panneau';

const ORTHO_DELTAS = [
  { x: 1, y: 0 },
  { x: -1, y: 0 },
  { x: 0, y: 1 },
  { x: 0, y: -1 },
];

export interface GridRect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function normalizeGridRect(x1: number, y1: number, x2: number, y2: number): GridRect {
  return {
    minX: Math.min(x1, x2),
    minY: Math.min(y1, y2),
    maxX: Math.max(x1, x2),
    maxY: Math.max(y1, y2),
  };
}

export function iterGridRectCells(rect: GridRect): Array<{ x: number; y: number }> {
  const cells: Array<{ x: number; y: number }> = [];
  for (let y = rect.minY; y <= rect.maxY; y++) {
    for (let x = rect.minX; x <= rect.maxX; x++) {
      cells.push({ x, y });
    }
  }
  return cells;
}

export function canPlacePropRect(circuit: Circuit, rect: GridRect): boolean {
  const cells = iterGridRectCells(rect);
  if (cells.length === 0) return false;

  const rectKeys = new Set(cells.map((cell) => cellCoordKey(cell.x, cell.y)));

  for (const cell of cells) {
    if (!isWithinGrid(circuit, cell.x, cell.y)) return false;
    if (isInfrastructureCell(circuit, cell.x, cell.y)) return false;
    if (
      isGrandstandCell(circuit, cell.x, cell.y) ||
      isSignCell(circuit, cell.x, cell.y) ||
      isDecor3dCell(circuit, cell.x, cell.y)
    ) {
      return false;
    }
  }

  const seeds = cells.filter(
    (cell) => canPlaceAmbientCell(circuit, cell.x, cell.y) || isPropCell(circuit, cell.x, cell.y)
  );
  if (seeds.length === 0) return false;

  const visited = new Set<string>();
  const queue = [...seeds];
  for (const cell of seeds) {
    visited.add(cellCoordKey(cell.x, cell.y));
  }

  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const delta of ORTHO_DELTAS) {
      const nx = current.x + delta.x;
      const ny = current.y + delta.y;
      const key = cellCoordKey(nx, ny);
      if (!rectKeys.has(key) || visited.has(key)) continue;
      visited.add(key);
      queue.push({ x: nx, y: ny });
    }
  }

  return visited.size === cells.length;
}

function newPropId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function stripDecorAtKeys(circuit: Circuit, keys: Set<string>): Circuit {
  const decorCells = (circuit.decorCells ?? []).filter(
    (cell) => !keys.has(cellCoordKey(cell.x, cell.y))
  );
  return {
    ...circuit,
    decorCells: decorCells.length > 0 ? decorCells : undefined,
  };
}

export function sanitizeSignText(text: string): string {
  return text.trim().slice(0, MAX_SIGN_TEXT_LENGTH);
}

export function findGrandstandAt(circuit: Circuit, x: number, y: number): CircuitGrandstand | null {
  for (const stand of circuit.grandstands ?? []) {
    if (stand.cells.some((cell) => cell.x === x && cell.y === y)) return stand;
  }
  return null;
}

export function findSignAt(circuit: Circuit, x: number, y: number): CircuitSign | null {
  for (const sign of circuit.signs ?? []) {
    if (x >= sign.x && y >= sign.y && x < sign.x + sign.width && y < sign.y + sign.height) {
      return sign;
    }
  }
  return null;
}

export function findDecor3dAt(circuit: Circuit, x: number, y: number): CircuitDecor3d | null {
  for (const decor of circuit.decor3d ?? []) {
    if (decor.cells.some((cell) => cell.x === x && cell.y === y)) return decor;
  }
  return null;
}

export function addGrandstandRect(
  circuit: Circuit,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): { circuit: Circuit } | { error: string } {
  const rect = normalizeGridRect(x1, y1, x2, y2);
  const cells = iterGridRectCells(rect);
  if (!canPlacePropRect(circuit, rect)) {
    return { error: 'Zone invalide pour les gradins' };
  }
  const keys = new Set(cells.map((cell) => cellCoordKey(cell.x, cell.y)));
  const withoutDecor = stripDecorAtKeys(circuit, keys);
  const grandstand: CircuitGrandstand = { id: newPropId('gs'), cells };
  return {
    circuit: {
      ...withoutDecor,
      grandstands: [...(withoutDecor.grandstands ?? []), grandstand],
    },
  };
}

export function addSignRect(
  circuit: Circuit,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  text: string
): { circuit: Circuit } | { error: string } {
  const rect = normalizeGridRect(x1, y1, x2, y2);
  const width = rect.maxX - rect.minX + 1;
  const height = rect.maxY - rect.minY + 1;
  const label = sanitizeSignText(text) || DEFAULT_SIGN_LABEL;
  if (!canPlacePropRect(circuit, rect)) {
    return { error: 'Zone invalide pour le panneau' };
  }
  const keys = new Set(iterGridRectCells(rect).map((cell) => cellCoordKey(cell.x, cell.y)));
  const withoutDecor = stripDecorAtKeys(circuit, keys);
  const sign: CircuitSign = {
    id: newPropId('sg'),
    x: rect.minX,
    y: rect.minY,
    width,
    height,
    text: label,
  };
  return {
    circuit: {
      ...withoutDecor,
      signs: [...(withoutDecor.signs ?? []), sign],
    },
  };
}

export function addDecor3dRect(
  circuit: Circuit,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  kind: CircuitDecor3dKind,
  modelId: string,
  rotation: Direction
): { circuit: Circuit } | { error: string } {
  const rect = normalizeGridRect(x1, y1, x2, y2);
  const cells = iterGridRectCells(rect);
  if (!canPlacePropRect(circuit, rect)) {
    return { error: 'Zone invalide pour le décor' };
  }
  const keys = new Set(cells.map((cell) => cellCoordKey(cell.x, cell.y)));
  const withoutDecor = stripDecorAtKeys(circuit, keys);
  const decor: CircuitDecor3d = { id: newPropId('d3'), kind, modelId, rotation, cells };
  return {
    circuit: {
      ...withoutDecor,
      decor3d: [...(withoutDecor.decor3d ?? []), decor],
    },
  };
}

export function removeDecor3d(circuit: Circuit, decorId: string): Circuit {
  const decor3d = (circuit.decor3d ?? []).filter((decor) => decor.id !== decorId);
  return {
    ...circuit,
    decor3d: decor3d.length > 0 ? decor3d : undefined,
  };
}

export function updateSignText(circuit: Circuit, signId: string, text: string): Circuit {
  const label = sanitizeSignText(text);
  if (!label) return circuit;
  const signs = circuit.signs;
  if (!signs?.length) return circuit;
  return {
    ...circuit,
    signs: signs.map((sign) => (sign.id === signId ? { ...sign, text: label } : sign)),
  };
}

export function removeGrandstand(circuit: Circuit, grandstandId: string): Circuit {
  const grandstands = (circuit.grandstands ?? []).filter((stand) => stand.id !== grandstandId);
  return {
    ...circuit,
    grandstands: grandstands.length > 0 ? grandstands : undefined,
  };
}

export function removeSign(circuit: Circuit, signId: string): Circuit {
  const signs = (circuit.signs ?? []).filter((sign) => sign.id !== signId);
  return {
    ...circuit,
    signs: signs.length > 0 ? signs : undefined,
  };
}

export function removePropAt(circuit: Circuit, x: number, y: number): Circuit {
  const stand = findGrandstandAt(circuit, x, y);
  if (stand) return removeGrandstand(circuit, stand.id);
  const sign = findSignAt(circuit, x, y);
  if (sign) return removeSign(circuit, sign.id);
  if (findVehicleAt(circuit, x, y)) return removeVehicleAt(circuit, x, y);
  const decor = findDecor3dAt(circuit, x, y);
  if (decor) return removeDecor3d(circuit, decor.id);
  const roadPiece = findRoadPieceAt(circuit, x, y);
  if (roadPiece) return removeRoadPiece(circuit, roadPiece.id);
  return circuit;
}

export function clearGrandstands(circuit: Circuit): Circuit {
  if (!circuit.grandstands?.length) return circuit;
  return { ...circuit, grandstands: undefined };
}

export function clearSigns(circuit: Circuit): Circuit {
  if (!circuit.signs?.length) return circuit;
  return { ...circuit, signs: undefined };
}

export function clearAllCircuitAmbience(circuit: Circuit): Circuit {
  return {
    ...circuit,
    decorCells: undefined,
    grandstands: undefined,
    signs: undefined,
    vehicles: undefined,
    decor3d: undefined,
  };
}

export function pruneCircuitProps(circuit: Circuit): Circuit {
  let next = circuit;

  const grandstands = (next.grandstands ?? [])
    .map((stand) => ({
      ...stand,
      cells: stand.cells.filter(
        (cell) => isWithinGrid(next, cell.x, cell.y) && !isInfrastructureCell(next, cell.x, cell.y)
      ),
    }))
    .filter((stand) => stand.cells.length > 0);
  next = {
    ...next,
    grandstands: grandstands.length > 0 ? grandstands : undefined,
  };

  const usedGrandstand = grandstandCellKeys(next);
  const signs = (next.signs ?? []).filter((sign) => {
    if (!isWithinGrid(next, sign.x, sign.y)) return false;
    for (let dy = 0; dy < sign.height; dy++) {
      for (let dx = 0; dx < sign.width; dx++) {
        const x = sign.x + dx;
        const y = sign.y + dy;
        if (!isWithinGrid(next, x, y)) return false;
        if (isInfrastructureCell(next, x, y)) return false;
        if (usedGrandstand.has(cellCoordKey(x, y))) return false;
      }
    }
    return sanitizeSignText(sign.text).length > 0;
  });
  next = {
    ...next,
    signs: signs.length > 0 ? signs : undefined,
  };

  const decor3d = (next.decor3d ?? [])
    .map((decor) => ({
      ...decor,
      cells: decor.cells.filter(
        (cell) => isWithinGrid(next, cell.x, cell.y) && !isInfrastructureCell(next, cell.x, cell.y)
      ),
    }))
    .filter((decor) => decor.cells.length > 0);
  next = {
    ...next,
    decor3d: decor3d.length > 0 ? decor3d : undefined,
  };

  return pruneCircuitVehicles(next);
}
